import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Truck, 
  MapPin, 
  FileText, 
  BarChart3, 
  Shield,
  Clock,
  Users,
  Zap,
  CheckCircle,
  ArrowRight,
  Star,
  Phone,
  Mail,
  MapPinned,
  Wrench,
  Euro,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const features = [
    {
      icon: <MapPin className="h-8 w-8 text-blue-500" />,
      title: "GPS Echtzeit-Tracking",
      description: "Verfolgen Sie Ihre gesamte Flotte in Echtzeit. Optimieren Sie Routen und sparen Sie Kraftstoffkosten."
    },
    {
      icon: <FileText className="h-8 w-8 text-green-500" />,
      title: "Digitale Belegverwaltung",
      description: "Automatischer Export zu Lexware. Tankbelege und Rechnungen in Sekunden verarbeitet."
    },
    {
      icon: <AlertTriangle className="h-8 w-8 text-orange-500" />,
      title: "Schadensmanagementsystem",
      description: "Dokumentieren Sie Fahrzeugschäden bei Check-in/Check-out. Klären Sie Verantwortlichkeiten rechtssicher."
    },
    {
      icon: <Clock className="h-8 w-8 text-purple-500" />,
      title: "Zeiterfassung & Planung",
      description: "Automatische Arbeitszeiterfassung, Schichtplanung und Urlaubsverwaltung für Ihr Team."
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-cyan-500" />,
      title: "Lexware Integration",
      description: "Nahtlose Synchronisation mit Ihrem Lexware-Buchhaltungssystem. Keine doppelte Dateneingabe."
    },
    {
      icon: <Users className="h-8 w-8 text-pink-500" />,
      title: "Team-Kommunikation",
      description: "Integrierter Chat mit Walkie-Talkie Funktion. Schnelle Absprachen in Echtzeit."
    }
  ];

  const useCases = [
    {
      title: "Restaurants & Gastronomie",
      description: "Lieferfahrzeuge tracken, Belege automatisch verwalten",
      icon: "🍔"
    },
    {
      title: "Lieferdienste",
      description: "Routen optimieren, Lieferungen nachverfolgen",
      icon: "📦"
    },
    {
      title: "Handwerksbetriebe",
      description: "Werkzeuge und Fahrzeuge zentral verwalten",
      icon: "🔧"
    },
    {
      title: "Pflegedienste",
      description: "Tourenplanung und Zeiterfassung optimieren",
      icon: "⚕️"
    }
  ];

  const testimonials = [
    {
      name: "Michael Weber",
      company: "Weber Lieferservice GmbH",
      text: "Seit wir flotto.app nutzen, sparen wir 30% Zeit bei der Verwaltung. Die Lexware-Integration ist Gold wert!",
      rating: 5
    },
    {
      name: "Sandra Müller",
      company: "Müller's Bäckerei",
      text: "Endlich haben wir den Überblick über unsere 15 Lieferfahrzeuge. Das Schadensmanagementsystem hat uns mehrfach vor Ärger bewahrt.",
      rating: 5
    },
    {
      name: "Thomas Schneider",
      company: "Schneider Handwerk",
      text: "Intuitive Bedienung, faire Preise und super Support. Kann ich nur empfehlen!",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "Wie lange dauert die Einrichtung?",
      answer: "Die Grundeinrichtung dauert nur 15 Minuten. Fahrzeuge und Mitarbeiter können Sie sofort hinzufügen. Unser Support-Team hilft Ihnen gerne bei der Einrichtung."
    },
    {
      question: "Benötige ich spezielle Hardware?",
      answer: "Nein! flotto.app funktioniert komplett webbasiert. Für GPS-Tracking können Sie optional GPS-Tracker nutzen, aber die meisten Funktionen funktionieren auch ohne zusätzliche Hardware."
    },
    {
      question: "Ist meine Daten sicher?",
      answer: "Absolut! Alle Daten werden verschlüsselt in deutschen Rechenzentren gespeichert. Wir sind vollständig DSGVO-konform und erfüllen höchste Sicherheitsstandards."
    },
    {
      question: "Kann ich jederzeit kündigen?",
      answer: "Ja, Sie können monatlich kündigen. Keine langfristigen Verträge, keine versteckten Kosten. Fair und transparent."
    },
    {
      question: "Funktioniert die Lexware-Integration automatisch?",
      answer: "Ja! Einmal eingerichtet, werden alle Belege automatisch zu Lexware synchronisiert. Sie sparen sich die manuelle Eingabe komplett."
    },
    {
      question: "Gibt es eine Mobile App?",
      answer: "Ja, flotto.app ist vollständig responsive und funktioniert perfekt auf Smartphones und Tablets. Eine dedizierte App folgt in Kürze."
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "19",
      period: "Monat",
      description: "Perfekt für kleine Unternehmen",
      features: [
        "Bis zu 5 Fahrzeuge",
        "10 Benutzer",
        "GPS Echtzeit-Tracking",
        "Digitale Belegverwaltung",
        "Schadensmeldung",
        "E-Mail Support"
      ],
      cta: "Jetzt starten",
      popular: false
    },
    {
      name: "Professional",
      price: "79",
      period: "Monat",
      description: "Für wachsende Teams",
      features: [
        "Bis zu 20 Fahrzeuge",
        "Unbegrenzte Benutzer",
        "Alle Starter-Features",
        "Lexware-Integration",
        "Schadensmanagementsystem",
        "Zeiterfassung & Planung",
        "Team-Chat",
        "Prioritäts-Support"
      ],
      cta: "Beliebteste Wahl",
      popular: true
    },
    {
      name: "Enterprise",
      price: "Individuell",
      period: "",
      description: "Maßgeschneidert für Ihre Flotte",
      features: [
        "Unbegrenzte Fahrzeuge",
        "Unbegrenzte Benutzer",
        "Alle Professional-Features",
        "Dedizierter Account Manager",
        "API-Zugang",
        "Custom Features",
        "On-Premise Option",
        "24/7 Premium Support"
      ],
      cta: "Kontakt aufnehmen",
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-[#0a0a0a] to-gray-900">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/assets/flotto-icon.png" 
                alt="flotto.app" 
                className="h-10 w-10"
              />
              <span className="text-2xl font-bold text-white">flotto.app</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-300 hover:text-white transition-colors">Features</a>
              <a href="#preise" className="text-gray-300 hover:text-white transition-colors">Preise</a>
              <a href="#use-cases" className="text-gray-300 hover:text-white transition-colors">Branchen</a>
              <a href="#faq" className="text-gray-300 hover:text-white transition-colors">FAQ</a>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline" 
                className="border-gray-700 text-gray-300"
              >
                Login
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Kostenlos testen
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/50 px-4 py-2">
                <Zap className="h-4 w-4 mr-2 inline" />
                Made in Germany - DSGVO-konform
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                Fuhrparkverwaltung, die{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                  wirklich funktioniert
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                Die All-in-One Lösung für Ihr Flottenmanagement. GPS-Tracking, automatische Belege, 
                Schadensmeldung und Lexware-Integration in einer Software.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/register')}
                  size="lg" 
                  className="h-14 px-8 text-lg bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30"
                >
                  14 Tage kostenlos testen
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  onClick={() => navigate('/login')}
                  size="lg" 
                  variant="outline"
                  className="h-14 px-8 text-lg border-gray-700"
                >
                  Demo ansehen
                </Button>
              </div>

              <div className="flex items-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Keine Kreditkarte nötig
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Monatlich kündbar
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 blur-3xl"></div>
              <img 
                src="/assets/flotto-logo-full.png" 
                alt="flotto.app Dashboard" 
                className="relative z-10 rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 px-6 border-y border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-gray-400">Bereits über 500+ Unternehmen vertrauen auf flotto.app</p>
          </div>
          <div className="flex items-center justify-center gap-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
            ))}
            <span className="ml-3 text-gray-300 font-semibold">4.9 von 5.0</span>
            <span className="text-gray-500">(230+ Bewertungen)</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Alles, was Sie für Ihr Flottenmanagement brauchen
            </h2>
            <p className="text-xl text-gray-400">
              Professionelle Features zu fairen Preisen
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="bg-[#111] border-gray-800 hover:border-gray-700 transition-all group">
                <CardHeader>
                  <div className="mb-4 p-3 bg-gradient-to-br from-white/5 to-white/0 rounded-xl w-fit group-hover:scale-110 transition-transform">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl text-white">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="preise" className="py-20 px-6 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Transparent. Fair. Ohne versteckte Kosten.
            </h2>
            <p className="text-xl text-gray-400">
              Wählen Sie den Plan, der zu Ihrem Unternehmen passt
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative ${
                  plan.popular 
                    ? 'bg-gradient-to-b from-blue-900/20 to-[#111] border-blue-600 scale-105' 
                    : 'bg-[#111] border-gray-800'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white px-4 py-1">
                      Beliebteste Wahl
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-8 pt-8">
                  <CardTitle className="text-2xl text-white mb-2">{plan.name}</CardTitle>
                  <CardDescription className="text-gray-400">{plan.description}</CardDescription>
                  <div className="mt-4">
                    {plan.price === "Individuell" ? (
                      <div className="text-3xl font-bold text-white">Individuell</div>
                    ) : (
                      <>
                        <div className="text-5xl font-bold text-white">
                          {plan.price}€
                        </div>
                        <div className="text-gray-400 mt-1">pro {plan.period}</div>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                  <Button 
                    onClick={() => plan.price === "Individuell" ? window.location.href = 'mailto:Admin@zonik-solutions.de' : navigate('/register')}
                    className={`w-full mt-6 h-12 ${
                      plan.popular 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-800 hover:bg-gray-700'
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-4">
              💡 Alle Preise zzgl. MwSt. • 14 Tage kostenlos testen • Keine Kreditkarte erforderlich
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Perfekt für Ihre Branche
            </h2>
            <p className="text-xl text-gray-400">
              Egal ob Restaurant, Lieferdienst oder Handwerk - flotto.app passt sich an
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <Card key={index} className="bg-[#111] border-gray-800 hover:border-blue-600 transition-all group cursor-pointer">
                <CardContent className="pt-6 text-center">
                  <div className="text-6xl mb-4">{useCase.icon}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{useCase.title}</h3>
                  <p className="text-gray-400">{useCase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Was unsere Kunden sagen
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-[#111] border-gray-800">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-gray-500 text-sm">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Häufig gestellte Fragen
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="bg-[#111] border-gray-800">
                <CardContent className="p-0">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-900 transition-colors"
                  >
                    <span className="text-lg font-semibold text-white">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-900/20 to-purple-900/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Bereit, Ihr Flottenmanagement zu revolutionieren?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Starten Sie noch heute Ihre 14-tägige kostenlose Testphase
          </p>
          <Button 
            onClick={() => navigate('/register')}
            size="lg" 
            className="h-16 px-12 text-xl bg-blue-600 hover:bg-blue-700 shadow-xl"
          >
            Jetzt kostenlos starten
            <ArrowRight className="ml-3 h-6 w-6" />
          </Button>
          <p className="mt-6 text-gray-400">
            Keine Kreditkarte erforderlich • Monatlich kündbar • Support in Deutsch
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] border-t border-gray-800 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/assets/flotto-icon.png" alt="flotto.app" className="h-8 w-8" />
                <span className="text-xl font-bold text-white">flotto.app</span>
              </div>
              <p className="text-gray-400 text-sm">
                Die moderne Fuhrparkverwaltung für erfolgreiche Unternehmen.
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Produkt</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#preise" className="hover:text-white">Preise</a></li>
                <li><a href="#use-cases" className="hover:text-white">Branchen</a></li>
                <li><a href="#faq" className="hover:text-white">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Rechtliches</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="/impressum" className="hover:text-white">Impressum</a></li>
                <li><a href="/datenschutz" className="hover:text-white">Datenschutz</a></li>
                <li><a href="/agb" className="hover:text-white">AGB</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Kontakt</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:Admin@zonik-solutions.de" className="hover:text-white">
                    Admin@zonik-solutions.de
                  </a>
                </li>
                <li className="flex items-start gap-2">
                  <MapPinned className="h-4 w-4 mt-1" />
                  <span>
                    Friedrichshulder Weg 157A<br/>
                    25469 Halstenbek
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
            <p>© 2025 flotto.app - Ein Produkt von Zonik Solutions. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
